import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Database, Plus, Trash2, Pencil, X, Save, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

interface Dataset {
  id: string;
  name: string;
  description: string | null;
  fields: string[];
  created_at: string;
}

interface DataRecord {
  id: string;
  dataset_id: string;
  field_data: Record<string, string>;
}

export const DatasetManagement: React.FC = () => {
  const { user } = useAuth();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [records, setRecords] = useState<DataRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // Dataset form
  const [showDatasetDialog, setShowDatasetDialog] = useState(false);
  const [editingDataset, setEditingDataset] = useState<Dataset | null>(null);
  const [dsName, setDsName] = useState('');
  const [dsDescription, setDsDescription] = useState('');
  const [dsFields, setDsFields] = useState('');

  // Record form
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DataRecord | null>(null);
  const [recordFormData, setRecordFormData] = useState<Record<string, string>>({});

  const fetchDatasets = useCallback(async () => {
    const { data } = await supabase.from('data_datasets').select('*').order('name');
    if (data) {
      setDatasets(data.map(d => ({
        id: d.id,
        name: d.name,
        description: d.description,
        fields: d.fields || [],
        created_at: d.created_at,
      })));
    }
    setLoading(false);
  }, []);

  const fetchRecords = useCallback(async (datasetId: string) => {
    setRecordsLoading(true);
    const { data } = await supabase
      .from('data_records')
      .select('*')
      .eq('dataset_id', datasetId)
      .order('created_at');
    if (data) {
      setRecords(data.map(r => ({
        id: r.id,
        dataset_id: r.dataset_id,
        field_data: (r.field_data as Record<string, string>) || {},
      })));
    }
    setRecordsLoading(false);
  }, []);

  useEffect(() => { fetchDatasets(); }, [fetchDatasets]);

  useEffect(() => {
    if (selectedDataset) fetchRecords(selectedDataset.id);
    else setRecords([]);
  }, [selectedDataset, fetchRecords]);

  // Keep selectedDataset in sync
  useEffect(() => {
    if (!selectedDataset) return;
    const refreshed = datasets.find(d => d.id === selectedDataset.id);
    if (refreshed) setSelectedDataset(refreshed);
    else setSelectedDataset(null);
  }, [datasets]);

  const openNewDataset = () => {
    setEditingDataset(null);
    setDsName('');
    setDsDescription('');
    setDsFields('');
    setShowDatasetDialog(true);
  };

  const openEditDataset = (ds: Dataset) => {
    setEditingDataset(ds);
    setDsName(ds.name);
    setDsDescription(ds.description || '');
    setDsFields(ds.fields.join(', '));
    setShowDatasetDialog(true);
  };

  const handleSaveDataset = async () => {
    const fields = dsFields.split(',').map(f => f.trim()).filter(Boolean);
    if (!dsName.trim() || fields.length === 0) {
      toast.error('Name and at least one field are required');
      return;
    }

    if (editingDataset) {
      const { error } = await supabase
        .from('data_datasets')
        .update({ name: dsName.trim(), description: dsDescription.trim() || null, fields })
        .eq('id', editingDataset.id);
      if (error) { toast.error('Failed to update dataset'); return; }
      toast.success('Dataset updated');
    } else {
      const { error } = await supabase
        .from('data_datasets')
        .insert({ name: dsName.trim(), description: dsDescription.trim() || null, fields, created_by: user!.id });
      if (error) { toast.error('Failed to create dataset'); return; }
      toast.success('Dataset created');
    }
    setShowDatasetDialog(false);
    fetchDatasets();
  };

  const handleDeleteDataset = async (ds: Dataset) => {
    if (!confirm(`Delete dataset \"${ds.name}\" and all its records?`)) return;
    const { error } = await supabase.from('data_datasets').delete().eq('id', ds.id);
    if (error) { toast.error('Failed to delete dataset'); return; }
    toast.success('Dataset deleted');
    if (selectedDataset?.id === ds.id) setSelectedDataset(null);
    fetchDatasets();
  };

  const openNewRecord = () => {
    if (!selectedDataset) return;
    setEditingRecord(null);
    const empty: Record<string, string> = {};
    selectedDataset.fields.forEach(f => empty[f] = '');
    setRecordFormData(empty);
    setShowRecordDialog(true);
  };

  const openEditRecord = (rec: DataRecord) => {
    if (!selectedDataset) return;
    setEditingRecord(rec);
    const data: Record<string, string> = {};
    selectedDataset.fields.forEach(f => data[f] = rec.field_data[f] || '');
    setRecordFormData(data);
    setShowRecordDialog(true);
  };

  const handleSaveRecord = async () => {
    if (!selectedDataset) return;
    if (editingRecord) {
      const { error } = await supabase
        .from('data_records')
        .update({ field_data: recordFormData })
        .eq('id', editingRecord.id);
      if (error) { toast.error('Failed to update record'); return; }
      toast.success('Record updated');
    } else {
      const { error } = await supabase
        .from('data_records')
        .insert({ dataset_id: selectedDataset.id, field_data: recordFormData });
      if (error) { toast.error('Failed to create record'); return; }
      toast.success('Record added');
    }
    setShowRecordDialog(false);
    fetchRecords(selectedDataset.id);
  };

  const handleDeleteRecord = async (id: string) => {
    if (!selectedDataset) return;
    const { error } = await supabase.from('data_records').delete().eq('id', id);
    if (error) { toast.error('Failed to delete record'); return; }
    toast.success('Record deleted');
    fetchRecords(selectedDataset.id);
  };

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDataset) return;
    e.target.value = '';

    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) { toast.error('CSV must have a header row and at least one data row'); return; }

    // Skip sep= hint if present
    let startIdx = 0;
    if (lines[0].toLowerCase().startsWith('sep=')) startIdx = 1;

    // Auto-detect delimiter: tab, semicolon, or comma
    const headerLine = lines[startIdx];
    let delimiter = ',';
    if (headerLine.includes('\t')) delimiter = '\t';
    else if (headerLine.includes(';')) delimiter = ';';

    const headers = headerLine.split(delimiter).map(h => h.trim().replace(/^\uFEFF/, '').replace(/^["']|["']$/g, ''));

    const rows: Record<string, string>[] = [];
    for (let i = startIdx + 1; i < lines.length; i++) {
      const vals = lines[i].split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        if (selectedDataset.fields.includes(h)) {
          row[h] = (vals[idx] || '').trim();
        }
      });
      rows.push(row);
    }

    if (rows.length === 0) { toast.error('No valid data rows found'); return; }

    const { error } = await supabase
      .from('data_records')
      .insert(rows.map(r => ({ dataset_id: selectedDataset.id, field_data: r })));
    if (error) { toast.error('Failed to import CSV'); return; }
    toast.success(`Imported ${rows.length} records`);
    fetchRecords(selectedDataset.id);
  };

  const downloadCsvTemplate = () => {
    if (!selectedDataset) return;
    const bom = '\uFEFF';
    const csv = bom + selectedDataset.fields.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDataset.name}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Datasets</h2>
          <p className="text-sm text-muted-foreground">Create and manage data collections for label field lookups</p>
        </div>
        <Button onClick={openNewDataset} className="gap-2">
          <Plus className="w-4 h-4" /> New Dataset
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Datasets list */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="w-4 h-4" /> All Datasets
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              {datasets.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">No datasets yet</p>
              ) : (
                <div className="divide-y divide-border">
                  {datasets.map(ds => (
                    <button
                      key={ds.id}
                      onClick={() => setSelectedDataset(ds)}
                      className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${selectedDataset?.id === ds.id ? 'bg-primary/10' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{ds.name}</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); openEditDataset(ds); }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteDataset(ds); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {ds.description && <p className="text-xs text-muted-foreground mt-0.5">{ds.description}</p>}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {ds.fields.map(f => (
                          <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Records */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {selectedDataset ? `Records — ${selectedDataset.name}` : 'Select a dataset'}
              </CardTitle>
              {selectedDataset && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={downloadCsvTemplate}>
                    <Download className="w-3 h-3" /> Template CSV
                  </Button>
                  <label>
                    <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" asChild>
                      <span><Upload className="w-3 h-3" /> Import CSV</span>
                    </Button>
                    <input type="file" accept=".csv" className="hidden" onChange={handleImportCsv} />
                  </label>
                  <Button size="sm" className="gap-1 h-7 text-xs" onClick={openNewRecord}>
                    <Plus className="w-3 h-3" /> Add Record
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!selectedDataset ? (
              <p className="p-8 text-sm text-muted-foreground text-center">Select a dataset to view and manage records</p>
            ) : recordsLoading ? (
              <p className="p-8 text-sm text-muted-foreground text-center">Loading...</p>
            ) : records.length === 0 ? (
              <p className="p-8 text-sm text-muted-foreground text-center">No records yet. Add records manually or import a CSV.</p>
            ) : (
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectedDataset.fields.map(f => (
                        <TableHead key={f} className="text-xs">{f}</TableHead>
                      ))}
                      <TableHead className="w-20 text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map(rec => (
                      <TableRow key={rec.id}>
                        {selectedDataset.fields.map(f => (
                          <TableCell key={f} className="text-xs">{rec.field_data[f] || ''}</TableCell>
                        ))}
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditRecord(rec)}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteRecord(rec.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dataset dialog */}
      <Dialog open={showDatasetDialog} onOpenChange={setShowDatasetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDataset ? 'Edit Dataset' : 'New Dataset'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={dsName} onChange={e => setDsName(e.target.value)} placeholder="e.g. Products" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input value={dsDescription} onChange={e => setDsDescription(e.target.value)} placeholder="Optional description" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fields (comma-separated)</Label>
              <Input value={dsFields} onChange={e => setDsFields(e.target.value)} placeholder="e.g. ProductName, SKU, Price" />
              <p className="text-[10px] text-muted-foreground">These should match your label template field names</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDatasetDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveDataset} className="gap-1"><Save className="w-4 h-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record dialog */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit Record' : 'Add Record'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedDataset?.fields.map(field => (
              <div key={field} className="space-y-1.5">
                <Label className="text-xs">{field}</Label>
                <Input
                  value={recordFormData[field] || ''}
                  onChange={e => setRecordFormData(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={`Enter ${field}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRecord} className="gap-1"><Save className="w-4 h-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
